import {useMemo, useState} from "react";
import {categoryFilterKey, sameCategoryFilter, type SearchCategoryFacet, type SearchCategoryFilter} from "@zaaaz/shared";
import {useI18n} from "./I18nProvider";

type Props = {
  facets: SearchCategoryFacet[];
  selected?: SearchCategoryFilter | null;
  loading?: boolean;
  open?: boolean;
  onToggle?: () => void;
  onChange: (next: SearchCategoryFilter | null) => void;
};

function filterFacetTree(nodes: SearchCategoryFacet[], q: string): SearchCategoryFacet[] {
  const needle = q.trim().toLowerCase();
  if(!needle) return nodes;
  const out: SearchCategoryFacet[] = [];
  for(const node of nodes) {
    const childMatches = node.children ? filterFacetTree(node.children, needle) : [];
    const selfMatch = node.nume.toLowerCase().includes(needle);
    if(selfMatch || childMatches.length) {
      out.push({
        ...node,
        children: childMatches.length ? childMatches : selfMatch ? node.children : []
      });
    }
  }
  return out;
}

function facetContainsSelection(node: SearchCategoryFacet, selected?: SearchCategoryFilter | null): boolean {
  if(sameCategoryFilter(selected, node.filter)) return true;
  return !!node.children?.some((child) => facetContainsSelection(child, selected));
}

function FacetNode({
  node,
  depth,
  selected,
  expandAll,
  onPick
}: {
  node: SearchCategoryFacet;
  depth: number;
  selected?: SearchCategoryFilter | null;
  expandAll?: boolean;
  onPick: (filter: SearchCategoryFilter | null) => void;
}) {
  const hasChildren = !!node.children?.length;
  const [open, setOpen] = useState(hasChildren && (!!expandAll || facetContainsSelection(node, selected)));
  const active = sameCategoryFilter(selected, node.filter);
  return (
    <li className="cc-filters__catItem">
      <div className="cc-filters__catRow" style={{paddingLeft: depth ? depth * 10 : 0}}>
        {hasChildren ? (
          <button type="button" className="cc-filters__catToggle" onClick={() => setOpen(!open)} aria-expanded={open} aria-label={node.nume}>
            <i className={`fa fa-chevron-${open ? "down" : "right"}`} aria-hidden="true" />
          </button>
        ) : (
          <span className="cc-filters__catToggle cc-filters__catToggle--spacer" aria-hidden="true" />
        )}
        <button
          type="button"
          className={`cc-filters__option cc-filters__option--tree${active ? " isActive" : ""}`}
          onClick={() => onPick(active ? null : node.filter)}
          aria-pressed={active}
        >
          <span className="cc-filters__optionBox" aria-hidden="true" />
          <span className="cc-filters__optionLabel">{node.nume}</span>
          <span className="cc-filters__catFilterCount">{node.count}</span>
        </button>
      </div>
      {hasChildren && open ? (
        <ul className="cc-filters__catSub">
          {node.children!.map((child) => (
            <FacetNode key={child.url} node={child} depth={depth + 1} selected={selected} expandAll={expandAll} onPick={onPick} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

export function SearchCategoryFilters({facets, selected, loading = false, open = true, onToggle, onChange}: Props) {
  const {t} = useI18n();
  const [catQ, setCatQ] = useState("");
  const filtered = useMemo(() => filterFacetTree(facets, catQ), [facets, catQ]);
  const showSkeleton = loading && !facets.length;
  if(!showSkeleton && !facets.length) return null;
  return (
    <div className="cc-filters__section cc-filters__section--searchCats">
      <button type="button" className="cc-filters__sectionHead" onClick={onToggle} aria-expanded={open}>
        <span>{t("CATEGORIES")}</span>
        <span className="cc-filters__sectionMeta">
          {selected?.c ? <span className="cc-filters__sectionBadge">1</span> : null}
          <span className="cc-filters__sectionToggle" aria-hidden="true">{open ? "−" : "+"}</span>
        </span>
      </button>
      {open ? (
        <div className="cc-filters__sectionBody">
          {showSkeleton ? (
            <div className="cc-searchOverlay__catSk" aria-hidden="true">
              {Array.from({length: 5}, (_, i) => (
                <div key={i} className="cc-searchOverlay__catSkLine" />
              ))}
            </div>
          ) : (
            <>
              <div className="cc-filters__search">
                <input
                  type="search"
                  className="cc-filters__searchInput"
                  placeholder={t("Search categories...")}
                  value={catQ}
                  onChange={(e) => setCatQ(e.target.value)}
                />
              </div>
              <ul className="cc-filters__catList cc-filters__catScroll cc-filters__options cc-filters__options--tall">
                {filtered.length ? filtered.map((node) => (
                  <FacetNode
                    key={categoryFilterKey(node.filter) || node.url}
                    node={node}
                    depth={0}
                    selected={selected}
                    expandAll={!!catQ.trim()}
                    onPick={onChange}
                  />
                )) : (
                  <li><p className="cc-filters__empty">{t("No products found")}</p></li>
                )}
              </ul>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
